import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { z } from "zod";
import { Button, Group, Modal, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useMutation } from "@tanstack/react-query";
import { useTRPC } from "../trpc.js";
import { useAppAction, useAppSelector } from "../hooks/redux.js";
import { updateInitStyle } from "../reducers/initReducer.js";
import { getInitStyle } from "../reducers/initReducer.js";
import { redirectToStyle } from "../utils/redirect.js";
import { notifications } from "@mantine/notifications";
export const RenameStyleModal = ({ opened, onClose, currentName }) => {
    const intl = useIntl();
    const trpc = useTRPC();
    const style = useAppSelector(getInitStyle);
    const updateCurrentStyle = useAppAction(updateInitStyle);
    const { mutateAsync: updateStyleMutation } = useMutation(trpc.updateStyle.mutationOptions());
    const [isPending, setIsPending] = useState(false);
    const validate = zod4Resolver(z.object({
        displayName: z
            .string()
            .min(1, {
            message: intl.formatMessage({
                defaultMessage: "Name is required",
                id: "error.nameRequired",
            }),
        })
            .max(255, {
            message: intl.formatMessage({
                defaultMessage: "Name is too long",
                id: "error.nameTooLong",
            }),
        }),
    }));
    const { onSubmit, getInputProps } = useForm({
        validateInputOnBlur: true,
        initialValues: {
            displayName: currentName,
        },
        validate,
    });
    const handleRename = useCallback(({ displayName }) => {
        const trimmed = displayName.trim();
        if (trimmed === currentName) {
            onClose();
            return;
        }
        setIsPending(true);
        void updateStyleMutation({
            styleId: style.id,
            update: { displayName: trimmed },
        })
            .then((result) => {
            updateCurrentStyle(result.style);
            redirectToStyle(result);
            onClose();
        })
            .catch((error) => {
            if (error instanceof Error) {
                notifications.show({
                    title: intl.formatMessage({
                        defaultMessage: "Failed to update style",
                        id: "notification.failedToUpdateStyle",
                    }),
                    message: error.message,
                });
            }
        })
            .finally(() => {
            setIsPending(false);
        });
    }, [currentName, style, updateStyleMutation, updateCurrentStyle, onClose]);
    return (_jsx(Modal, { opened: opened, onClose: onClose, title: intl.formatMessage({ defaultMessage: "Rename map", id: "title.renameMap" }), children: _jsx("form", { onSubmit: onSubmit(handleRename), children: _jsxs(Stack, { gap: "md", children: [_jsx(TextInput, { label: intl.formatMessage({
                            defaultMessage: "Name",
                            id: "label.name",
                        }), required: true, ...getInputProps("displayName") }), _jsxs(Group, { gap: "md", justify: "flex-end", mt: "md", children: [_jsx(Button, { variant: "secondary", onClick: onClose, disabled: isPending, children: _jsx(FormattedMessage, { id: "button.cancel", defaultMessage: "Cancel" }) }), _jsx(Button, { variant: "tertiary", type: "submit", loading: isPending, children: _jsx(FormattedMessage, { id: "button.rename", defaultMessage: "Rename" }) })] })] }) }) }));
};
